import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useSelector, useDispatch } from 'react-redux';
import { createSelector } from 'reselect';
import Slider from '@mui/material/Slider';
import {
  PlayArrow,
  Pause,
} from '@mui/icons-material';
import eachYearOfInterval from 'date-fns/eachYearOfInterval';
import getYear from 'date-fns/getYear';
import startOfYear from 'date-fns/startOfYear';
import addYears from 'date-fns/addYears';

const mapState = createSelector(
  ({ timelinePlaying }) => timelinePlaying,
  ({ timelines }) => timelines,
  ({ layersLoading }) => Object.keys(layersLoading).join(''),
  (timelinePlaying, timelines, loadingLayersString) => ({
    timelinePlaying,
    timelines,
    loadingLayersString,
  }),
);

const TimelineContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: center;
`;

const margin = {
  margin: 16,
};

export const Timeline = () => {
  const { timelines, loadingLayersString, timelinePlaying } = useSelector(
    mapState,
  );

  const timeline = Object.values(timelines)[0];

  const start = new Date(timeline.start);
  const end = startOfYear(
    addYears(new Date(timeline.end), 1),
  );
  const yearDates = eachYearOfInterval({ start, end });
  const max = yearDates.length;

  const [yearIndex, setYearIndex] = useState(max);

  const timelineLayersLoading =
  yearDates[yearIndex - 1] &&
  loadingLayersString.indexOf(yearDates[yearIndex - 1].toDateString()) !== -1;

  const dispatch = useDispatch();
  const setPlaying = useCallback(
    (payload) => {
      dispatch({ type: 'SET_PLAYING', payload });
      dispatch({ type: 'CLEAR_LOADING_LAYERS' });
    },
    [dispatch],
  );

  const marks = yearDates.reduce((acc, year, i) => {
    if (i === 0) return acc;
    acc.push({
      value: i,
      label: `‘${
        `${getYear(year) - 1}`.slice(2, 4)
      }`,
    });

    return acc;
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      if (timelinePlaying && !timelineLayersLoading) {
        // if the next layer is the last layer, go back to the first
        let nextYearIndex = yearIndex + 1;
        if (yearIndex + 1 >= yearDates.length) {
          nextYearIndex = 1;
        }

        dispatch({
          type: 'SELECT_DATE_RANGE',
          payload: {
            start: yearDates[nextYearIndex - 1],
            end: yearDates[nextYearIndex],
          },
        });

        setYearIndex(nextYearIndex);
      }
    }, 1000);

    return () => {
      clearInterval(interval);
    };
  }, [yearIndex, dispatch, yearDates, timelineLayersLoading, timelinePlaying]);

  const onChange = useCallback(
    (e, value) => {
      setPlaying(false);
      const newValue = parseInt(value, 10);
      setYearIndex(newValue);

      dispatch({ type: 'CLEAR_LOADING_LAYERS' });
      dispatch({
        type: 'SELECT_DATE_RANGE',
        payload: {
          start: yearDates[newValue - 1],
          end: yearDates[newValue],
        },
      });
    },
    [setPlaying, dispatch, yearDates],
  );

  return (
    <TimelineContainer>
      {timelinePlaying ? (
        <Pause style={margin} onClick={() => setPlaying(false)} />
      ) : (
        <PlayArrow style={margin} onClick={() => setPlaying(true)} />
      )}
      <Slider
        value={yearIndex}
        aria-labelledby="discrete-slider-always"
        valueLabelFormat={(v) => `20${v}`}
        valueLabelDisplay="off"
        style={{
          maxWidth: 560,
          width: '100%',
          ...margin,
        }}
        onChange={onChange}
        step={1}
        marks={marks}
        min={1}
        max={max - 1}
      />
    </TimelineContainer>
  );
};
